import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, FileDown } from 'lucide-react';
import apiService, { getApiErrorMessage, MetadataExport } from '../services/api';
import { getExportDeliveryAuthority } from '../utils/exportDelivery';
import './JobCardCompact.css';

interface ExportJobCardCompactProps {
  job: MetadataExport;
  isExpanded?: boolean;
  onToggleExpand?: (exportId: string) => void;
}

const getStatusTone = (status?: string): 'success' | 'warning' | 'danger' | 'muted' => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed' || normalized === 'succeeded') return 'success';
  if (normalized === 'failed' || normalized === 'dead_letter') return 'danger';
  if (['processing', 'running', 'leased', 'retrying', 'queued', 'pending'].includes(normalized)) return 'warning';
  return 'muted';
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTimeFull = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getProgressPercent = (job: MetadataExport): number => {
  if (job.status === 'completed' || job.status === 'failed') return 100;
  const queueStatus = String(job.queue_status || '').toLowerCase();
  if (queueStatus === 'running') return 65;
  if (queueStatus === 'leased') return 30;
  if (queueStatus === 'failed') return 15;
  if (queueStatus === 'queued') return 5;
  return job.status === 'processing' ? 10 : 0;
};

const ExportJobCardCompact: React.FC<ExportJobCardCompactProps> = ({
  job,
  isExpanded: controlledExpanded,
  onToggleExpand,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  useEffect(() => {
    setDownloadError(null);
  }, [job.export_id]);

  const handleToggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand(job.export_id);
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only handle keys that originated on the header, not bubbled from nested controls
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleExpand();
    }
  };
  const displayStatus = job.queue_status || job.status;
  const statusTone = getStatusTone(displayStatus);
  const progressPercent = getProgressPercent(job);
  const deliveryAuthority = getExportDeliveryAuthority(job);
  const canDownload = deliveryAuthority === 'authenticated_download';

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDownload || isDownloading) {
      return;
    }
    setDownloadError(null);
    setIsDownloading(true);
    try {
      await apiService.downloadExport(job.export_id);
    } catch (error) {
      console.error('Failed to download metadata export:', error);
      setDownloadError(getApiErrorMessage(
        error,
        'Failed to download the metadata export. Please try again.',
      ));
    } finally {
      setIsDownloading(false);
    }
  };

  const exportParams = job.export_params || {};
  const hasExportParams = Object.keys(exportParams).length > 0;

  return (
    <article
      className={`job-card-compact ${isExpanded ? 'expanded' : ''}`}
      data-tone={statusTone}
      data-job-id={job.export_id}
    >
      <div
        className="job-card-compact-header"
        role="button"
        tabIndex={0}
        onClick={handleToggleExpand}
        onKeyDown={handleKeyDown}
      >
        <div className="job-card-row">
          <div className="job-card-left">
            <span className="expand-icon">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
            <span className="job-icon">
              <FileDown size={14} />
            </span>
            <span className="job-name" title={job.target_id}>
              {job.target_id || job.export_id.slice(0, 8)}
            </span>
          </div>

          <div className="job-progress-bar-container">
            <div className="job-progress-bar">
              <div
                className={`progress-segment ${statusTone === 'success' ? 'success' : statusTone === 'danger' ? 'failed' : 'in-progress'}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="job-card-right job-card-export-metadata">
            <span className="job-type-badge">{job.export_type}</span>
            <span className="job-time">{formatDateTime(job.created_at)}</span>
            <span className="job-attempts">
              {job.attempts ?? 0}/{job.max_attempts ?? 1}
            </span>
            {canDownload && (
              <button
                className="download-btn-inline"
                onClick={handleDownload}
                title="Download"
                disabled={isDownloading}
              >
                <Download size={12} />
              </button>
            )}
            <span className={`job-status-badge ${statusTone}`} title={displayStatus}>
              {displayStatus}
            </span>
          </div>
        </div>
      </div>

      {downloadError && (
        <div
          className="export-download-error error-text"
          role="alert"
          aria-live="polite"
        >
          {downloadError}
        </div>
      )}

      {isExpanded && (
        <div className="job-card-compact-details">
          {/* Row 1: Core identifiers */}
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Export ID</span>
              <span className="detail-value mono" title={job.export_id}>{job.export_id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Export Type</span>
              <span className="detail-value">{job.export_type}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Target ID</span>
              <span className="detail-value mono">{job.target_id || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className="detail-value">{job.status}</span>
            </div>
          </div>

          {/* Row 2: Queue info */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Queue Status</span>
              <span className="detail-value">{job.queue_status || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Attempts</span>
              <span className="detail-value">{job.attempts ?? 0} / {job.max_attempts ?? 1}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">File Size</span>
              <span className="detail-value">{formatBytes(job.file_size_bytes)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">File Size (Bytes)</span>
              <span className="detail-value mono">{job.file_size_bytes ?? 'N/A'}</span>
            </div>
          </div>

          {/* Row 3: Timestamps */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Created At</span>
              <span className="detail-value">{formatDateTimeFull(job.created_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Available At</span>
              <span className="detail-value">{formatDateTimeFull(job.available_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Started At</span>
              <span className="detail-value">{formatDateTimeFull(job.started_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Completed At</span>
              <span className="detail-value">{formatDateTimeFull(job.completed_at)}</span>
            </div>
          </div>

          {/* Row 4: Updated */}
          <div className="details-grid" style={{ marginTop: '10px' }}>
            <div className="detail-item">
              <span className="detail-label">Updated At</span>
              <span className="detail-value">{formatDateTimeFull(job.updated_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Progress</span>
              <span className="detail-value">{progressPercent}%</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Has Download</span>
              <span className="detail-value">{canDownload ? 'Yes' : 'No'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Has GCS URI</span>
              <span className="detail-value">{job.gcs_uri ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {/* GCS URI */}
          {job.gcs_uri && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">GCS URI</span>
              <span className="detail-value mono">{job.gcs_uri}</span>
            </div>
          )}

          {/* Download method */}
          {canDownload && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Download Method</span>
              <span className="detail-value">Authenticated API</span>
            </div>
          )}

          {/* Export params */}
          {hasExportParams && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Export Parameters</span>
              <pre className="raw-json">{JSON.stringify(exportParams, null, 2)}</pre>
            </div>
          )}

          {/* Error message */}
          {job.error_message && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Error Message</span>
              <span className="detail-value error-text">{job.error_message}</span>
            </div>
          )}

          {/* Last error */}
          {job.last_error && job.last_error !== job.error_message && (
            <div className="detail-row-full" style={{ marginTop: '10px' }}>
              <span className="detail-label">Last Error</span>
              <span className="detail-value error-text">{job.last_error}</span>
            </div>
          )}

          {/* Full job as JSON */}
          <div className="detail-row-full" style={{ marginTop: '10px' }}>
            <span className="detail-label">Full Export Data (Raw)</span>
            <pre className="raw-json">{JSON.stringify(job, null, 2)}</pre>
          </div>

          {canDownload && (
            <button
              className="download-btn"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <Download size={14} />
              {isDownloading ? 'Downloading…' : 'Download Export'}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export default ExportJobCardCompact;
